/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { format, isAfter, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { CustomButton, WheelPicker } from "../../components";
import "./styles.scss";

const DateFilterModal = ({
  visible,
  onClickFilter,
  onClickReset,
  onClickBackdrop,
  prevFromDate,
  prevToDate,
}) => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState({});
  const [showWheelPicker, setShowWheelPicker] = useState(false);
  const [wheelPickerProps, setWheelPickerProps] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  const handleOnConfirm = (e) => {
    const { name, value } = e.target;
    if (name === "fromDate") {
      setDateRange((prev) => ({ ...prev, toDate: null }));
    }
    setDateRange((prevState) => ({ ...prevState, [name]: value }));
    setShowWheelPicker(false);
  };

  const handleOnClickDate = ({ dateName }) => {
    setErrorMessage("");
    setWheelPickerProps({
      onClose: () => setShowWheelPicker(false),
      onConfirm: handleOnConfirm,
      name: dateName,
      value: dateRange[dateName],
    });
    setShowWheelPicker(true);
  };

  const validateDateRange = () => {
    const fromDate = parseISO(dateRange.fromDate);
    const toDate = parseISO(dateRange.toDate);

    return isAfter(toDate, fromDate);
  };

  const checkAndSetDateValidationError = () => {
    if (dateRange.fromDate && dateRange.toDate) {
      const isValid = validateDateRange();
      if (!isValid) {
        setErrorMessage(t("date-filter-modal.date_filter_error_message_1"));
      }
      return isValid;
    } else {
      setErrorMessage(t("date-filter-modal.date_filter_error_message_2"));
      return false;
    }
  };

  const handleOnClickFilter = () => {
    const isValid = checkAndSetDateValidationError();
    if (isValid) {
      onClickFilter(dateRange);
    }
  };

  const handleOnClickReset = () => {
    setDateRange({});
    setErrorMessage("");
    onClickReset();
  };

  useEffect(() => {
    if (dateRange.fromDate && dateRange.toDate) {
      checkAndSetDateValidationError();
    }
  }, [dateRange]);

  useEffect(() => {
    if (prevFromDate && prevToDate) {
      setDateRange({ fromDate: prevFromDate, toDate: prevToDate });
    }
  }, [prevFromDate, prevToDate]);

  if (!visible) return null;

  return (
    <div>
      {showWheelPicker ? (
        <WheelPicker
          isModalOpen={showWheelPicker}
          onClose={wheelPickerProps.onClose}
          onConfirm={wheelPickerProps.onConfirm}
          name={wheelPickerProps.name}
          value={wheelPickerProps.value}
        />
      ) : (
        <div className="date-filter-modal-container" onClick={onClickBackdrop}>
          <div
            className="date-filter-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="date-filter-heading-wrapper">
              <span className="date-filter-heading">{t("global.filter")}</span>
              <span className="date-filter-reset" onClick={handleOnClickReset}>
                {t("global.reset_filter")}
              </span>
            </div>
            <p className="date-filter-description">
              {t("date-filter-modal.date_filter_description")}
            </p>
            <div className="date-filter-input-container">
              <p className="date-filter-input-label">{t("global.date")}</p>
              <div className="date-filter-input-wrapper">
                <div
                  className="date-filter-input"
                  onClick={() => handleOnClickDate({ dateName: "fromDate" })}
                >
                  {dateRange?.fromDate ? (
                    <span className="date-filter-input-value">
                      {format(new Date(dateRange.fromDate), "dd/MM/yyyy")}
                    </span>
                  ) : (
                    <span className="date-filter-input-placeholder">
                      {t("global.from")}
                    </span>
                  )}
                </div>
                <div
                  className="date-filter-input"
                  onClick={() => handleOnClickDate({ dateName: "toDate" })}
                >
                  {dateRange?.toDate ? (
                    <span className="date-filter-input-value">
                      {format(new Date(dateRange.toDate), "dd/MM/yyyy")}
                    </span>
                  ) : (
                    <span className="date-filter-input-placeholder">
                      {t("global.to")}
                    </span>
                  )}
                </div>
              </div>
              {errorMessage ? (
                <p className="date-filter-input-error">{errorMessage}</p>
              ) : null}
            </div>
            <CustomButton
              text={t("global.filter")}
              onClick={handleOnClickFilter}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterModal;
